import React, { useState, useEffect } from 'react';
import { api } from '../context/AppContext';
import { Button } from './ui/button';
import { 
  Tag, 
  DollarSign, 
  ChevronDown, 
  ChevronUp,
  Loader2,
  Star,
  ArrowRightLeft,
  Sparkles
} from 'lucide-react';

/**
 * Component to display potential savings when recipe ingredients are on sale.
 * Shows star ingredient badge, deals list, and smart substitution suggestions.
 */
const RecipeSavingsAlert = ({ recipeId, language = 'fr' }) => {
  const [savingsData, setSavingsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!recipeId) return;
    
    const fetchSavings = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/deals/match-recipe/${recipeId}`);
        setSavingsData(response.data);
      } catch (err) {
        console.error('Error fetching savings:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSavings();
  }, [recipeId]);

  // Loading state
  if (loading) {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center gap-2">
        <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
        <span className="text-sm text-emerald-400">
          {language === 'fr' ? 'Recherche d\'aubaines...' : 'Looking for deals...'}
        </span>
      </div>
    );
  }

  // No data or error
  if (error || !savingsData) {
    return null;
  }

  const { 
    matches, 
    total_potential_savings, 
    match_count, 
    star_ingredient,
    substitution_suggestions,
    has_substitutions,
    total_substitution_savings,
    flyer_validity 
  } = savingsData;

  // Nothing to show
  if (!savingsData.has_savings && !has_substitutions) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Star Ingredient Badge - Always visible if exists */}
      {star_ingredient && (
        <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/40 rounded-xl p-4 flex items-center gap-4">
          <div className="p-2 bg-amber-500/30 rounded-lg">
            <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-amber-300/70 uppercase tracking-wider font-medium">
              {language === 'fr' ? 'Ingrédient Vedette en Spécial' : 'Star Ingredient on Sale'}
            </p>
            <p className="text-base font-bold text-amber-100">
              {star_ingredient.deal_name}
            </p>
            <p className="text-sm text-amber-300">
              <span className="font-bold">-{star_ingredient.discount_percent}%</span> {language === 'fr' ? 'chez' : 'at'} {star_ingredient.store_logo} {star_ingredient.store_name}
            </p>
          </div>
        </div>
      )}

      {/* Main Savings Banner - If there are direct matches */}
      {match_count > 0 && (
        <div className="bg-gradient-to-r from-emerald-500/15 to-green-500/15 border border-emerald-500/30 rounded-xl overflow-hidden">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full p-4 flex items-center justify-between hover:bg-emerald-500/5 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-emerald-400">
                  {language === 'fr' 
                    ? `💰 ${match_count} ingrédient(s) de cette recette en spécial!` 
                    : `💰 ${match_count} ingredient(s) from this recipe on sale!`}
                </p>
                <p className="text-lg font-bold text-emerald-300">
                  {language === 'fr' 
                    ? `Économise jusqu'à ${total_potential_savings}$` 
                    : `Save up to $${total_potential_savings}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground hidden sm:block">
                {language === 'fr' ? 'Voir les aubaines' : 'See deals'}
              </span>
              {expanded ? (
                <ChevronUp className="w-5 h-5 text-emerald-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-emerald-400" />
              )}
            </div>
          </button>

          {/* Expanded Details */}
          {expanded && (
            <div className="border-t border-emerald-500/20 p-4 space-y-3">
              {flyer_validity && (
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' 
                    ? `Valide du ${flyer_validity.start} au ${flyer_validity.end}` 
                    : `Valid from ${flyer_validity.start} to ${flyer_validity.end}`}
                </p>
              )}

              <div className="space-y-2">
                {matches.map((match, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{match.deal.store_logo}</span>
                      <div>
                        <p className="text-sm font-medium">{match.deal.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {match.deal.store_name} • {match.deal.unit}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground line-through">
                          {match.deal.original_price}$
                        </span>
                        <span className="text-sm font-bold text-emerald-400">
                          {match.deal.sale_price}$
                        </span>
                      </div>
                      <span className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">
                        -{match.deal.discount_percent}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Smart Substitution Suggestions */}
      {has_substitutions && substitution_suggestions.length > 0 && (
        <div className="bg-gradient-to-r from-purple-500/15 to-blue-500/15 border border-purple-500/30 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-purple-300">
                {language === 'fr' ? '💡 Substitution Intelligente du Chef' : '💡 Chef\'s Smart Substitution'}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {substitution_suggestions.map((suggestion, index) => (
              <div 
                key={index}
                className="p-3 bg-secondary/50 rounded-lg"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-muted-foreground capitalize">{suggestion.original_category}</span>
                  <ArrowRightLeft className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-medium text-purple-300 capitalize">{suggestion.substitute_category}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{suggestion.substitute_deal.store_logo}</span>
                    <div>
                      <p className="text-sm">{suggestion.substitute_deal.name}</p>
                      <p className="text-xs text-muted-foreground">{suggestion.substitute_deal.store_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-purple-400">
                      {suggestion.substitute_deal.sale_price}$
                    </span>
                    <p className="text-xs text-purple-300">
                      {language === 'fr' ? `Économise ~${suggestion.estimated_savings}$` : `Save ~$${suggestion.estimated_savings}`}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-center text-purple-300/70 italic">
            {language === 'fr' 
              ? '* Le Chef suggère cette substitution pour maximiser vos économies sans compromettre la qualité du plat' 
              : '* Chef suggests this substitution to maximize savings without compromising dish quality'}
          </p>
        </div>
      )}
    </div>
  );
};

export default RecipeSavingsAlert;
